import Page, { PageActionsType } from "components/common/page/Page";
import PersonView from "components/entities/app/person/PersonView";
import { useParams } from "react-router-dom";
import Error from 'components/common/Error';
import { faPencil, faRefresh, faTrash } from "@fortawesome/free-solid-svg-icons";
import { AppRoutes, resolveRoute } from "config/router";
import useAppContext from "utilities/hooks/useAppContext";
import { Roles } from "utilities/app-utilities";
import Tabs from "components/common/tabs/Tabs";
import Tab from "components/common/tabs/Tab";
import { ComponentStateTypes } from "components/common/ComponentState";
import { useEffect, useState } from "react";
import Loading from "components/common/loading/Loading";
import { graph } from "config/axios-context";
import { PersonViewQuery, PersonViewQueryVariables, SaleFilterInput, SortEnumType, WorkerFilterInput } from "types/graphql";
import { gql } from "@apollo/client";
import { PersonQueryResponseToPersonType, PersonType } from "types/person-types";
import DeleteConfirmationModal from "components/common/confirmation/DeleteConfirmationModal";
import SaleDatalist from "components/entities/agriculture/sale/SaleDatalist";
import BookingDatalist, {
    BookingDatalistPropsType
} from "../../components/entities/accommodation/booking/BookingDatalist";
import { uuid } from "config/app-constants";
import WorkerDatalist from "components/entities/app/work/WorkerDatalist";
import WalletDatalist, { WalletDatalistProps } from "components/entities/app/person/wallet/WalletDatalist";

const DELETE_MODAL_HASH = "person-view-delete-modal";

type PersonReferencesType = PersonType & {
    anySales: boolean,
    anyWorkers: boolean,
    anyBooking: boolean,
    anyWallets: boolean,
}

var query = gql`
query personView($id: Int, $lang: String!) {
  person(where: { id: { eq: $id } }) {
    id
    name
    note
    contactInfo {
      items {
        value
        link
        name
        id
        personId
      }
    }
    tags {
      items {
        id
        tag {
          id
          name
        }
      }
    }
    countryCode
    country {
      alpha3Code
      name
      flag
      translate(lang: $lang)
    }
    sales {
      any
    }
    workers {
      any
    }
    bookings {
      any
    }
    wallets {
        any
    }
  }
}
`;

const PersonViewPage = () => {
    const params = useParams<{ id: string }>();
    const context = useAppContext();
    const id = Number(params?.id);
    const [model, setModel] = useState<PersonReferencesType>();
    const [componentState, setComponentState] = useState<ComponentStateTypes>('loading');

    const saleFixedFilters: SaleFilterInput = {
        personId: {
            eq: id
        }
    };

    const workFixedFilters: WorkerFilterInput = {
        personId: {
            eq: id
        }
    }

    const bookingDatalistConfig: BookingDatalistPropsType = {
        name: "person-view-page",
        fixedFilters: {
            personId: {
                eq: id
            }
        },
        hasBottomNavigation: false,
        config: {
            dataSource: {
                sort: [
                    {
                        id: uuid(),
                        name: 'inDate',
                        direction: SortEnumType.Desc
                    }
                ]
            }
        }
    }

    const walletDatalistCofig: WalletDatalistProps = {
        name: 'person-view-page-wallet',
        fixedFilters: {
            personId: {
                eq: id
            }
        }
    }

    const pageActions: PageActionsType = [
        {
            icon: faRefresh,
            name: 'refresh',
            title: context.t('button.reload'),
            link: () => loadData().then()
        },
        {
            icon: faPencil,
            name: 'edit',
            title: context.t('button.edit'),
            link: resolveRoute(AppRoutes.PERSON_EDIT, { id: id }),
            hide: () => {
                return !context.userHasRole(Roles.PERSON_WRITE);
            }
        },
        {
            icon: faTrash,
            name: 'delete',
            title: context.t('button.delete'),
            link: () => {
                context.modal.open(DELETE_MODAL_HASH);
            },
            hide: () => {
                return model && (!context.userHasRole(Roles.PERSON_WRITE) || model.anySales || model.anyWorkers) ? true : false;
            },
            className: 'text-danger'
        }
    ];

    useEffect(() => {
        loadData().then();
    }, []);

    const loadData = async () => {
        setComponentState('loading');
        try {
            const response = await graph<PersonViewQuery, PersonViewQueryVariables>({
                query: query,
                variables: {
                    id: Number(params.id) || 0,
                    lang: context.language.code
                }
            });

            if (response.errors) {
                throw response.errors;
            }

            var person = PersonQueryResponseToPersonType(response) as PersonReferencesType;
            person.anyWorkers = response.data?.person?.workers?.any || false;
            person.anySales = response.data?.person?.sales?.any || false;
            person.anyBooking = response.data?.person?.bookings?.any || false;
            person.anyWallets = response.data?.person?.wallets?.any || false;
            setModel(person);
            setComponentState('');
        } catch (error: any) {
            context.notify('error', 'error.loading-data', Array.isArray(error) ? error[0].message : error.message);
            setComponentState('error');
        }
    }

    const renderView = (): React.ReactNode => {
        if (!model) return (<></>);
        return (
            <div className="w-full">
                <Tabs>
                    <Tab title="common.info" name="info">
                        <PersonView model={model!} />
                    </Tab>
                    <Tab title="pages.people.wallet.list" hide={!model?.anyWallets || !context.userHasRole(Roles.ADMIN)} name="wallets">
                        <WalletDatalist {...walletDatalistCofig} />
                    </Tab>
                    <Tab title="pages.agriculture.sale.list" hide={!model?.anySales || !context.userHasRole(Roles.AGRICULTURE_SALES_READ)} name="sales">
                        <SaleDatalist fixedFilters={saleFixedFilters} />
                    </Tab>
                    <Tab title="pages.agriculture.work.list" hide={!model?.anyWorkers || !context.userHasRole(Roles.AGRICULTURE_WORK_READ)} name="work">
                        <WorkerDatalist name="personView" fixedFilters={workFixedFilters} />
                    </Tab>
                    <Tab title="pages.accommodation.booking.list" hide={!model?.anyBooking || !context.userHasRole(Roles.ACCOMMODATION_BOOKINGS_READ)} name="booking">
                        <BookingDatalist {...bookingDatalistConfig} />
                    </Tab>
                </Tabs>
            </div>
        );
    }

    let render: React.ReactNode;
    switch (componentState) {
        case 'loading':
            render = <Loading />;
            break;
        case 'error':
            render = <Error message="error" />;
            break;
        default:
            render = renderView();
            break;
    }

    return <>
        <Page title="pages.people.view" wFull actions={[...pageActions]}>
            {render}
        </Page>
        <DeleteConfirmationModal 
            onAfterConfirmationExecution={() => context.navigate(-1)} 
            name={DELETE_MODAL_HASH} id={id} onConfirm='personDelete' 
        />
    </>;
}

export default PersonViewPage;
